import React, { useContext, useEffect, useMemo, useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { AuthContext } from "../contexts/auth";
import ComboboxAutocomplete from "./combobox_autocomplete";
import { listOrganizationalUnits } from "../services/api_org_unit";

const GENERIC_OPTION = {
  id: "__GENERIC__",
  name: "Nessuna unità (Generico)",
  __isGeneric: true,
};

export default function V2PairOrgUnit({
  value = null,                     // OU selezionata (oggetto) oppure null
  onChange,                         // (ou|null) => void
  label = "Unità organizzativa",
  helperText = "Opzionale. Se selezionata, il turno sarà associato a questa unità.",
  placeholder = "Seleziona unità organizzativa...",
  disabled = false,
  className = "",
  zIndex = "z-[60]",
  onlyActive = true,
  includeGenericOption = true,      // mostra “Nessuna unità (Generico)” nella lista
  genericLabel = "Nessuna unità (Generico)",
  showSelectedGenericPill = true,   // se value === null, mostra pill “Generico”
}) {
  const { token } = useContext(AuthContext);

  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(value);
  const [error, setError] = useState("");

  // sync controlled value
  useEffect(() => {
    setSelected(value || null);
  }, [value]);

  // fetch units
  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const res = await listOrganizationalUnits(token, onlyActive);
        if (!cancelled) setUnits(res?.data || []);
      } catch (e) {
        console.error("Error fetching units:", e);
        if (!cancelled) {
          setUnits([]);
          setError("Errore durante il caricamento delle unità organizzative.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, onlyActive]);

  const options = useMemo(() => {
    if (!includeGenericOption) return units;
    return [{ ...GENERIC_OPTION, name: genericLabel }, ...(units || [])];
  }, [units, includeGenericOption, genericLabel]);

  const handleSelect = (opt) => {
    // se seleziono generico -> null
    if (opt?.__isGeneric || opt?.id === GENERIC_OPTION.id) {
      setSelected(null);
      onChange?.(null);
      return;
    }

    setSelected(opt || null);
    onChange?.(opt || null);
  };

  const handleClear = () => {
    setSelected(null);
    onChange?.(null);
  };

  const borderState =
    disabled ? "border-gray-100 bg-gray-50" : "border-gray-200 bg-white";

  const showingGenericSelected = showSelectedGenericPill && value === null;

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold uppercase text-indigo-800">{label}</div>
          {helperText && (
            <div className="text-xs text-gray-500 mt-1">{helperText}</div>
          )}
        </div>
      </div>

      <div className={`mt-4 ${borderState}`}>
        {/* Se non c'è selezionata (o vuoi comunque permettere cambio) mostro sempre combobox */}
        <div className="w-full">
          <ComboboxAutocomplete
            options={options}
            placeholder={loading ? "Caricamento..." : placeholder}
            onChange={handleSelect}
            value={selected}
            bgActiveColor="bg-indigo-50"
            textActiveColor="text-gray-900"
            positionOptions="absolute"
            zIndex={zIndex}
            borderClass="border-gray-200 rounded-lg"
            borderInput={disabled ? "opacity-60 pointer-events-none" : ""}
            alwaysFirstIds={includeGenericOption ? [GENERIC_OPTION.id] : []}
          />
        </div>

        {(selected || showingGenericSelected) && (
            <div className="mt-3 flex items-center justify-between gap-3">
                {/* LEFT: Selezionata */}
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-semibold text-gray-600">Selezionata:</span>

                    <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700 truncate">
                        {selected ? selected.name : "Generico"}
                    </span>
                </div>

                {/* RIGHT: Rimuovi (solo se è selezionata una OU reale) */}
                {selected && (
                <button
                    type="button"
                    onClick={handleClear}
                    disabled={disabled}
                    className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    title="Rimuovi selezione"
                >
                    <TrashIcon className="h-4 w-4" />
                    Rimuovi
                </button>
                )}
            </div>
        )}
        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
      </div>
    </div>
  );
}